// Presentational + action sections for ConsultPanel (nsofar docs/33), split
// out for the per-function size/complexity limits. State that must survive a
// sync refresh (race banner, payment link) stays in ConsultPanel; transient
// form-open state lives here.

import { useState, type JSX } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { CHOSEN_VIA_LABELS, type ConsultBlock, type ConsultDoctorSummary } from "@hcm/core";
import { apiFetch } from "../../lib/api.js";
import {
  consultErrorBody, formatIstStamp, formatRangeIst, formatAmountMinor,
  type ConsultActionErrorBody,
} from "../../lib/consultOps.js";
import { ShortlistForm, ConfirmForm, SingleFieldForm, type ShortlistOptionInput, type ConfirmInput } from "./ConsultActionForms.js";
import { ConsultOptionsList } from "./ConsultOptionsList.js";

export type SendAction = (path: string, body?: unknown) => void;

// Route-path form keys, single source (also keeps sonarjs happy on repeats).
const FORMS = { shortlist: "shortlist", confirm: "confirm", joinLink: "join-link", needsReschedule: "needs-reschedule" } as const;
type FormKey = (typeof FORMS)[keyof typeof FORMS] | null;

const ACTION_BTN = "text-sm border border-slate-300 rounded-lg px-3 py-1.5 hover:bg-slate-50 disabled:opacity-40";

/**
 * The one mutation behind every consult.* relay button. Success/failure
 * semantics per docs/33: success only means "nsofar accepted the action" —
 * the panel refreshes from sync; a 409 already_confirmed is the §5.4
 * two-console race, surfaced via onRace (info, not an error toast).
 */
export function useConsultAct(srId: string, cb: {
  onPaymentLink: (link: string) => void;
  onRace: (body: ConsultActionErrorBody) => void;
  onSent: () => void;
}): ReturnType<typeof useMutation<{ ok: boolean; nsofar?: Record<string, unknown> | null }, unknown, { path: string; body?: unknown }>> {
  return useMutation({
    mutationFn: ({ path, body }: { path: string; body?: unknown }) =>
      apiFetch<{ ok: boolean; nsofar?: Record<string, unknown> | null }>(
        `/consult-ops/${srId}/${path}`, { method: "POST", body: JSON.stringify(body ?? {}) }),
    onSuccess: (res, vars) => {
      const link = (res.nsofar as { payment_link?: string } | null | undefined)?.payment_link;
      if (vars.path === "payment-link" && link) cb.onPaymentLink(link);
      toast.success("Sent to nsofar — the consult will update via sync.");
      cb.onSent();
    },
    onError: (e: unknown) => {
      const body = consultErrorBody(e);
      if (body?.error === "already_confirmed") { cb.onRace(body); return; }
      toast.error(body?.message ?? "Action failed — nothing was applied.");
    },
  });
}

export function Row({ label, value }: { label: string; value: string }): JSX.Element {
  return (
    <div className="flex gap-2">
      <span className="w-36 text-xs text-slate-500 shrink-0">{label}</span>
      <span className="text-sm text-slate-800 break-words min-w-0">{value}</span>
    </div>
  );
}

export function ConcernSection({ srId }: { srId: string }): JSX.Element {
  const [concern, setConcern] = useState<string | null>(null);
  const reveal = useMutation({
    mutationFn: () => apiFetch<{ concern_text: string }>(`/consult-ops/${srId}/concern`),
    onSuccess: (d) => setConcern(d.concern_text),
    onError: (e: unknown) => {
      const body = consultErrorBody(e);
      if (body?.error === "no_concern_on_record") setConcern("");
      else toast.error(body?.message ?? "Could not load the concern text.");
    },
  });
  if (concern !== null) {
    return <Row label="Concern" value={concern === "" ? "No concern recorded." : concern} />;
  }
  return (
    <div className="flex gap-2 items-center">
      <span className="w-36 text-xs text-slate-500 shrink-0">Concern</span>
      <button type="button" onClick={() => reveal.mutate()} disabled={reveal.isPending}
        className="text-xs text-navy-800 hover:underline disabled:opacity-50">
        {reveal.isPending ? "Loading…" : "Reveal (health data — access is logged)"}
      </button>
    </div>
  );
}

// eslint-disable-next-line complexity -- flat list of per-field presence checks from the §2 payload table; each branch is a one-line row.
export function ConsultInfoRows({ srId, nsofarSrId, consult, paymentLink }: {
  srId: string; nsofarSrId?: string | null; consult: ConsultBlock; paymentLink: string | null;
}): JSX.Element {
  const chosenVia = consult.chosen_via ? (CHOSEN_VIA_LABELS[consult.chosen_via] ?? consult.chosen_via) : null;
  const link = paymentLink ?? consult.payment_link;
  return (
    <>
      <Row label="nsofar ref" value={nsofarSrId ?? "—"} />
      <Row label="Specialty" value={consult.specialty || "—"} />
      <Row label="Mode" value={consult.mode} />
      {consult.preferred_ranges.length > 0 && (
        <Row label="Preferred windows" value={consult.preferred_ranges.map(formatRangeIst).join("  ·  ")} />
      )}
      <ConcernSection srId={srId} />
      {chosenVia && <Row label="Chosen via" value={chosenVia} />}
      {consult.status === "payment_pending" && (
        <Row label="Awaiting payment" value={`${formatAmountMinor(consult.amount_minor, consult.currency)}${consult.invoice_id ? ` · ${consult.invoice_id}` : ""}`} />
      )}
      {link && <Row label="Payment link" value={link} />}
      {consult.confirmed_datetime_ist && <Row label="Confirmed slot" value={formatIstStamp(consult.confirmed_datetime_ist)} />}
      {consult.join_link ? <Row label="Join link" value={consult.join_link} />
        : consult.has_join_link ? <Row label="Join link" value="Attached (delivered to the family by nsofar)" /> : null}
      {consult.callback_note && <Row label="Callback" value={consult.callback_note} />}
      {consult.note && <Row label="Reschedule note" value={consult.note} />}
      {consult.partner_booking_ref && <Row label="Partner ref (internal)" value={consult.partner_booking_ref} />}
      {consult.partner_cost_minor != null && <Row label="Partner cost (internal)" value={String(consult.partner_cost_minor)} />}
    </>
  );
}

export function ConsultOptionsSection({ consult, selectable, pending, onSelect }: {
  consult: ConsultBlock; selectable: boolean; pending: boolean; onSelect: (optionId: string) => void;
}): JSX.Element | null {
  const options = consult.options ?? [];
  if (options.length === 0) return null;
  return (
    <ConsultOptionsList
      options={options}
      chosenOptionId={consult.chosen?.option_id ?? null}
      selectable={selectable}
      pending={pending}
      onSelect={onSelect}
    />
  );
}

const FORM_FOR_VERB: Record<string, Exclude<FormKey, null>> = {
  shortlist: FORMS.shortlist, confirm: FORMS.confirm, join_link: FORMS.joinLink, needs_reschedule: FORMS.needsReschedule,
};
const ACTION_LABELS: Record<string, string> = {
  shortlist: "Build shortlist", payment_link: "Get payment link",
  confirm: "Confirm booking", join_link: "Attach join link",
  needs_reschedule: "Needs reschedule", complete: "Mark completed",
};

function CompleteButton({ enabled, pending, onSend }: { enabled: boolean; pending: boolean; onSend: SendAction }): JSX.Element {
  const [confirming, setConfirming] = useState(false);
  if (!confirming) {
    return <button type="button" disabled={!enabled} onClick={() => setConfirming(true)}
      className={ACTION_BTN}>{ACTION_LABELS["complete"]}</button>;
  }
  return (
    <span className="flex items-center gap-2">
      <button type="button" disabled={pending} onClick={() => { onSend("complete"); setConfirming(false); }}
        className="text-xs font-medium text-white bg-navy-700 hover:bg-navy-800 rounded px-2 py-1 disabled:opacity-50">Confirm completed</button>
      <button type="button" className="text-xs text-slate-500 hover:underline" onClick={() => setConfirming(false)}>Cancel</button>
    </span>
  );
}

function ActionForm({ form, pending, onSend, doctors }: {
  form: Exclude<FormKey, null>; pending: boolean; onSend: SendAction; doctors: ConsultDoctorSummary[];
}): JSX.Element {
  switch (form) {
    case FORMS.shortlist:
      return <ShortlistForm pending={pending} doctors={doctors}
        onSubmit={(options: ShortlistOptionInput[]) => onSend(FORMS.shortlist, { options })} />;
    case FORMS.confirm:
      return <ConfirmForm pending={pending} onSubmit={(body: ConfirmInput) => onSend(FORMS.confirm, body)} />;
    case FORMS.joinLink:
      return <SingleFieldForm label="Video join link" placeholder="https://…" required buttonLabel="Relay join link"
        pending={pending} onSubmit={(v) => onSend(FORMS.joinLink, { join_link: v })} />;
    case FORMS.needsReschedule:
      return <SingleFieldForm label="Note for nsofar (optional)" placeholder="Chosen slot no longer available…" required={false}
        buttonLabel="Mark needs reschedule" pending={pending}
        onSubmit={(v) => onSend(FORMS.needsReschedule, v ? { note: v } : {})} />;
  }
}

export function ConsultActions({ verbs, enabled, pending, onSend }: {
  verbs: readonly string[]; enabled: boolean; pending: boolean; onSend: SendAction;
}): JSX.Element | null {
  const [openForm, setOpenForm] = useState<FormKey>(null);
  // Doctors mirror (docs/33 §4 amendment) — lazily fetched when the shortlist
  // form opens; feeds the "pick a known doctor" picker.
  const { data: doctorsData } = useQuery<{ data: ConsultDoctorSummary[] }>({
    queryKey: ["consult-doctors"],
    queryFn: () => apiFetch<{ data: ConsultDoctorSummary[] }>("/consult-ops/doctors?limit=200"),
    enabled: openForm === FORMS.shortlist,
    staleTime: 60_000,
  });
  if (verbs.length === 0) return null;
  const formVerbs = verbs.filter((v) => v in FORM_FOR_VERB);
  return (
    <div className="pt-2 border-t border-slate-100 space-y-3">
      <div className="flex items-center gap-2 flex-wrap">
        {verbs.includes("payment_link") && (
          <button type="button" disabled={!enabled || pending} onClick={() => onSend("payment-link")}
            className={ACTION_BTN}>{ACTION_LABELS["payment_link"]}</button>
        )}
        {formVerbs.map((verb) => {
          const form = FORM_FOR_VERB[verb];
          if (!form) return null;
          const isOpen = openForm === form;
          return (
            <button key={verb} type="button" disabled={!enabled}
              onClick={() => setOpenForm(isOpen ? null : form)}
              className={`${ACTION_BTN} ${isOpen ? "border-navy-400 text-navy-800" : ""}`}>
              {ACTION_LABELS[verb]}
            </button>
          );
        })}
        {verbs.includes("complete") && <CompleteButton enabled={enabled} pending={pending} onSend={onSend} />}
        {!enabled && (
          <span className="text-xs text-slate-400">
            Actions are disabled until the nsofar joint smoke passes (feature flag).
          </span>
        )}
      </div>
      {enabled && openForm !== null && (
        <ActionForm form={openForm} pending={pending} onSend={onSend} doctors={doctorsData?.data ?? []} />
      )}
    </div>
  );
}
